// Shop Builder "Product Grid" block with its own presentation options
// (layout, columns per device, first count / batch size, order, pagination
// or infinite scroll). Products come from PageRenderer's shop store
// (server pagination through getLakayUser), so the shop's category, search
// and filter controls keep driving the same list.
import React, { useEffect, useRef } from "react";
import ProductGridShop from "../ProductGridShop";
import { SHOP_GRID_SORTS, isLegacyShopGridConfig, normalizeShopGridOptions, shopSortKeyOf } from "./shopProductGridOptions";
import { resolveBlockLoadingMode, useViewportDevice } from "../../shops/productDisplay";

const ShopProductGridBlock = ({
  options = {},
  storeState = {},
  storeActions = {},
  products = [],
  siseProduct = 0,
  shopId = null,
  infoSource,
  laguangeElakay,
  Card,
  ButtonPagination,
  elakayEmptyBox,
}) => {
  const config = normalizeShopGridOptions(options);
  const sort = SHOP_GRID_SORTS[config.sortKey];
  const pageSize = config.initialCount;
  const currentSortKey = shopSortKeyOf(storeState.sort);
  const requestedRef = useRef("");

  // First page = this block's count and order. Requested once per
  // configuration; the store then keeps them for category/search changes.
  useEffect(() => {
    const wanted = `${config.sortKey}:${pageSize}`;
    const current = `${currentSortKey}:${storeState.limit}`;
    if (wanted === current || requestedRef.current === wanted || typeof storeActions.loadProducts !== "function") return;
    requestedRef.current = wanted;
    storeActions.loadProducts({ skip: 0, limit: pageSize, sort });
  }, [config.sortKey, pageSize, currentSortKey, storeState.limit, storeActions, sort]);

  const infinite = config.loadingMode === "infinite";
  const currentSkip = Number.isFinite(storeState.skip) ? storeState.skip : 0;
  return (
    <ProductGridShop
      currentShopId={shopId}
      loading={Boolean(storeState.loading)}
      productsByArrival={products}
      skip={infinite ? 0 : currentSkip}
      limit={pageSize}
      siseProduct={siseProduct}
      size={Number.isFinite(storeState.size) ? storeState.size : siseProduct}
      prodCat={storeState.prodCat || options.prodCat || "Tous les Produits"}
      infoSource={infoSource}
      laguangeElakay={laguangeElakay}
      Card={Card}
      ButtonPagination={ButtonPagination}
      loadMore={() => storeActions.loadMore?.(pageSize)}
      loadMore0={() => storeActions.loadMore0?.(pageSize)}
      elakayEmptyBox={elakayEmptyBox}
      gridAnimated={options.gridAnimated === true}
      animateCards={options.animateCards === true}
      resultAnimated={options.resultAnimated === true}
      resultStyle={options.resultStyle || "gradient"}
      resultBackground={options.resultBackground || null}
      showResultIcon={options.showResultIcon !== false}
      showResultBadge={options.showResultBadge !== false}
      showResultPageInfo={infinite ? false : options.showResultPageInfo !== false}
      gridStyle={options.gridStyle || "minimal"}
      gridBackground={options.gridBackground || null}
      columns={config.columns}
      emptyCount={options.emptyCount ?? 12}
      loadingMode={config.loadingMode}
      onLoadMoreAsync={infinite ? () => storeActions.appendShopProducts?.({ limit: config.batchSize }) : null}
      appending={Boolean(storeState.appending)}
      forcedCardMode={config.layout === "grid" ? "portrait" : config.layout === "list" ? "landscape" : null}
    />
  );
};

// Chooses, for the current device, between the untouched historical
// shopProductGrid rendering (renderLegacy) and the configurable block:
// element choice > boutique My Shop choice > historical grid, all gated by
// the Developer authorization (shops/productDisplay.js).
export const ShopProductGridSwitch = ({ options = {}, allSettings, shopDisplay, previewDevice = null, renderLegacy, renderBlock }) => {
  const viewportDevice = useViewportDevice();
  const device = previewDevice || viewportDevice;
  const loadingMode = resolveBlockLoadingMode({ allSettings, shopDisplay, options, device });
  const effective = { ...options, loadingMode };
  return (
    <div data-product-display-mode={loadingMode === "infinite" ? "infinite" : "grid"} data-product-display-device={device} style={{ display: "contents" }}>
      {isLegacyShopGridConfig(normalizeShopGridOptions(effective)) ? renderLegacy() : renderBlock(effective)}
    </div>
  );
};

export default ShopProductGridBlock;
